import React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import {
  FiActivity,
  FiBarChart2,
  FiRefreshCw,
  FiUsers,
  FiTrendingUp,
  FiMap,
  FiClock,
} from 'react-icons/fi';
import api from '../../config/api';
import { formatLocalDateTime } from '../../utils/formatTimestamp';
import ListPager from '../ListPager';
import { useClientPager } from '../../hooks/useClientPager';
import { ButtonSpinner } from '../LoadingButton';

function formatLabel(key) {
  if (!key) return '—';
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDateTime(value) {
  return formatLocalDateTime(value);
}

function AdoptionBar({ pct }) {
  const width = Math.min(100, Math.max(0, Number(pct) || 0));
  return (
    <div className="pa-bar" title={`${width}%`}>
      <motion.div
        className="pa-bar-fill"
        initial={{ width: 0 }}
        animate={{ width: `${width}%` }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      />
      <span>{width}%</span>
    </div>
  );
}

const ProductAnalyticsDashboard = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, error } = useQuery(
    ['product-analytics-summary'],
    () => api.get('/product-analytics/summary').then((r) => r.data),
    { staleTime: 30_000, refetchOnWindowFocus: true }
  );

  const userAdoption = data?.user_adoption || [];
  const {
    pageItems: pagedAdoption,
    pagination: adoptionPagination,
    setPage: setAdoptionPage,
  } = useClientPager(userAdoption);

  const refresh = () => queryClient.invalidateQueries(['product-analytics-summary']);

  if (isLoading && !data) {
    return <div className="loading">Loading product analytics…</div>;
  }

  if (error && !data) {
    return (
      <div className="pa-error">
        Failed to load analytics: {error.response?.data?.error || error.message}
      </div>
    );
  }

  const totals = data?.totals || {};
  const byDay = data?.by_day || [];
  const byRole = data?.by_role || [];
  const topFeatures = data?.top_features || [];
  const topPages = data?.top_pages || [];
  const maxDayEvents = Math.max(1, ...byDay.map((d) => Number(d.events) || 0));

  return (
    <div className="pa-dashboard">
      <div className="pa-toolbar">
        <div className="pa-window-note">
          <FiClock aria-hidden />
          Showing last {data?.window_days || 7} days. Full event history is retained in the database for custom queries.
        </div>
        <button type="button" className="btn btn-secondary" onClick={refresh}>
          {isFetching ? <ButtonSpinner /> : <FiRefreshCw />} Refresh
        </button>
      </div>

      <div className="pa-kpi-grid">
        <motion.div className="pa-kpi" layout>
          <FiUsers aria-hidden />
          <div>
            <span>Active users</span>
            <strong>{totals.unique_users ?? 0}</strong>
            <em>of {totals.active_registered_users ?? 0} registered</em>
          </div>
        </motion.div>
        <motion.div className="pa-kpi" layout>
          <FiTrendingUp aria-hidden />
          <div>
            <span>Adoption</span>
            <strong>{totals.adoption_pct ?? 0}%</strong>
            <em>users with activity</em>
          </div>
        </motion.div>
        <motion.div className="pa-kpi" layout>
          <FiActivity aria-hidden />
          <div>
            <span>Events</span>
            <strong>{totals.events ?? 0}</strong>
            <em>
              {totals.page_views ?? 0} views · {totals.feature_actions ?? 0} actions
            </em>
          </div>
        </motion.div>
        <motion.div className="pa-kpi" layout>
          <FiMap aria-hidden />
          <div>
            <span>Sessions</span>
            <strong>{totals.unique_sessions ?? 0}</strong>
            <em>distinct sessions</em>
          </div>
        </motion.div>
      </div>

      <div className="pa-panels">
        <section className="pa-panel">
          <h2>
            <FiBarChart2 aria-hidden /> Activity by day
          </h2>
          {byDay.length === 0 ? (
            <p className="pa-empty">No events in this window yet.</p>
          ) : (
            <ul className="pa-day-chart">
              {byDay.map((day) => {
                const events = Number(day.events) || 0;
                const height = Math.max(8, (events / maxDayEvents) * 100);
                return (
                  <li key={day.day}>
                    <div className="pa-day-col" title={`${events} events · ${day.users} users`}>
                      <motion.div
                        className="pa-day-bar"
                        initial={{ height: 0 }}
                        animate={{ height: `${height}%` }}
                        transition={{ duration: 0.4 }}
                      />
                    </div>
                    <span className="pa-day-label">{String(day.day).slice(5)}</span>
                    <span className="pa-day-users">{day.users}u</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="pa-panel">
          <h2>
            <FiUsers aria-hidden /> Adoption by role
          </h2>
          {byRole.length === 0 ? (
            <p className="pa-empty">No role breakdown yet.</p>
          ) : (
            <table className="pa-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Users</th>
                  <th>Events</th>
                </tr>
              </thead>
              <tbody>
                {byRole.map((row) => (
                  <tr key={row.role}>
                    <td>{formatLabel(row.role)}</td>
                    <td>{row.users}</td>
                    <td>{row.events}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <div className="pa-panels">
        <section className="pa-panel pa-panel--wide">
          <h2>Most used functionality</h2>
          {topFeatures.length === 0 ? (
            <p className="pa-empty">No feature usage yet.</p>
          ) : (
            <table className="pa-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Users</th>
                  <th>Events</th>
                  <th>Adoption</th>
                </tr>
              </thead>
              <tbody>
                {topFeatures.map((row) => (
                  <tr key={row.feature_key}>
                    <td>{formatLabel(row.feature_key)}</td>
                    <td>{row.users}</td>
                    <td>{row.events}</td>
                    <td>
                      <AdoptionBar pct={row.adoption_pct} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="pa-panel">
          <h2>Top pages</h2>
          {topPages.length === 0 ? (
            <p className="pa-empty">No page views yet.</p>
          ) : (
            <table className="pa-table">
              <thead>
                <tr>
                  <th>Path</th>
                  <th>Views</th>
                  <th>Users</th>
                </tr>
              </thead>
              <tbody>
                {topPages.map((row) => (
                  <tr key={row.path}>
                    <td className="pa-mono">{row.path}</td>
                    <td>{row.events}</td>
                    <td>{row.users}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <section className="pa-panel">
        <h2>User adoption (last {data?.window_days || 7} days)</h2>
        {userAdoption.length === 0 ? (
          <p className="pa-empty">No per-user activity yet.</p>
        ) : (
          <table className="pa-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Events</th>
                <th>Features</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {pagedAdoption.map((row) => (
                <tr key={row.user_id}>
                  <td>{row.user_email || `User #${row.user_id}`}</td>
                  <td>{formatLabel(row.user_role)}</td>
                  <td>{row.events}</td>
                  <td>{row.features_used}</td>
                  <td>{formatDateTime(row.last_seen_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <ListPager
          page={adoptionPagination.page}
          pageCount={adoptionPagination.pageCount}
          total={adoptionPagination.total}
          limit={adoptionPagination.limit}
          onPageChange={setAdoptionPage}
        />
      </section>
    </div>
  );
};

export default ProductAnalyticsDashboard;
